import { API_BASE_URL } from "@/config";
import { LOGOUT } from "@/utils/logout.js";
import { 
  SIDEBAR_ICONS, 
  getSidebarConfig, 
  filterMenuItems 
} from "@/components/sidebar/sidebarConfig.js";

export default {
    mixins: [LOGOUT],
    data: () => ({
      user_info: {},
      sidebarIcons: SIDEBAR_ICONS,
      unreadCommsCount: 0,
      activeDropdowns: {},
      clientName: null,
      domain: window.location.hostname,
      activeFeatureFlags: []
    }),

    created() {
      // Determine client name from domain
      const domain = window.location.hostname;
      if (domain.includes("verify")) {
        this.clientName = "verify";
      } else if (domain.includes("mobiafrica")) {
        this.clientName = "mobiafrica";
      } else if (domain.includes("suzuki")) {
        this.clientName = "suzuki";
      } else if (domain.includes("autocity")) {
        this.clientName = "autocity";
      } else if (domain.includes("yumbi")) {
        this.clientName = "yumbi";
      } else if (domain.includes("santam")) {
        this.clientName = "santam";
      } else if (domain.includes("altron")) {
        this.clientName = "altron";
      } else if (domain.includes("swift")) {
        this.clientName = "swift";
      } else if (domain.includes("webuycars")) {
        this.clientName = "webuycars";
      } else if (domain.includes("getsavvi")) {
        this.clientName = "getsavvi";
      }
    },

    computed: {
      /**
       * Get the current sidebar configuration based on role, client, and context
       */
      sidebarConfig() {
        if (!this.user_info.user_role) return { sections: [] };

        const context = {
          routeName: this.$route.name,
          projectId: this.$store.getters.get_project_id,
          workflowId: this.$store.getters.get_workflow_id,
          clientId: this.$store.getters.get_client_id
        };

        return getSidebarConfig(
          this.user_info.user_role,
          this.clientName,
          context
        );
      },

      /**
       * Processed sections with filtered items
       */
      processedSections() {
        if (!this.sidebarConfig.sections) return [];

        const context = {
          routeName: this.$route.name,
          projectId: this.$store.getters.get_project_id,
          activeFeatureFlags: this.activeFeatureFlags
        };

        return this.sidebarConfig.sections
          .filter(section => {
            // Check if section has route-based conditions
            if (section.conditions && section.conditions.routeNames) {
              const isInRouteList = section.conditions.routeNames.includes(context.routeName);
              // If inverse is true, show section when NOT in route list
              if (section.conditions.inverse) {
                return !isInRouteList;
              }
              return isInRouteList;
            }
            return true; // Show section if no conditions
          })
          .map(section => {
            const filteredItems = filterMenuItems(
              section.items,
              this.user_info.user_role,
              context
            );

            return {
              ...section,
              items: filteredItems
            };
          })
          .filter(section => section.items.length > 0); // Remove empty sections
      },

      /**
       * Get badge values for dynamic badges
       */
      badgeValues() {
        return {
          unreadComms: this.unreadCommsCount,
          unreadWorkbench: this.$store.getters.get_unread_workbench_count
        };
      }
    },
    watch: {
      $route: {
        immediate: true,
        handler() {
          this.updateDropdownStates();
        }
      }
    },

    methods: {
      /**
       * Handle logout action
       */
      route_to() {
        this.logout();
      },

      /**
       * Toggle dropdown visibility
       */
      toggleDropdown(dropdownId) {
        this.activeDropdowns[dropdownId] = !this.activeDropdowns[dropdownId];
      },

      /**
       * Check if dropdown is open
       */
      isDropdownOpen(dropdownId) {
        return this.activeDropdowns[dropdownId] || false;
      },

      /**
       * Check if dropdown has active child
       */
      hasActiveChild(children) {
        if (!children) return false;
        return children.some(child => child.routeName === this.$route.name);
      },

      /**
       * Update dropdown states based on active routes
       */
      updateDropdownStates() {
        this.processedSections.forEach(section => {
          section.items.forEach(item => {
            if (item.type === 'dropdown' && item.children) {
              const hasActive = this.hasActiveChild(item.children);
              if (hasActive) {
                this.activeDropdowns[item.id] = true;
              }
            }
          });
        });
      },

      /**
       * Check if dropdown should be shown (open or has active child)
       */
      shouldShowDropdown(item) {
        return this.isDropdownOpen(item.id) || this.hasActiveChild(item.children);
      },

      /**
       * Get user information from API
       */
      get_user_information() {
        const TOKEN = localStorage.getItem('token');
        const GET_USER_INFORMATION_API = `${API_BASE_URL}/verify_api/system_management_api/get_user_api/`;
  
        $.ajax({
          url: GET_USER_INFORMATION_API,
          method: 'GET',
          contentType: 'application/json',
          headers: {
            Authorization: `Token ${TOKEN}`
          },
          success: (response) => {
            this.user_info = response.data;
          },
          error: () => {
            console.error('Failed to fetch user information');
          }
        });
      },

      /**
       * Get icon for menu item
       */
      getIcon(iconKey) {
        return this.sidebarIcons[iconKey] || this.sidebarIcons.list;
      },

      /**
       * Check if icon is a Font Awesome icon
       */
      isFontAwesomeIcon(icon) {
        return typeof icon === 'string' && icon.startsWith('fa-');
      },

      /**
       * Get badge value for item
       */
      getBadgeValue(badgeKey) {
        if (!badgeKey) return null;
        return this.badgeValues[badgeKey] || null;
      },

      /**
       * Check if route is active
       */
      isRouteActive(routeName) {
        return this.$route.name === routeName;
      },

      /**
       * Fetch unread communications count for badge
       */
      fetchUnreadCommsCount() {
        const TOKEN = localStorage.getItem('token');
        const GET_UNREAD_COUNT = `${API_BASE_URL}/verify_api/system_management_api/get_unread_communications_count_api/`;

        $.ajax({
          url: GET_UNREAD_COUNT,
          method: 'GET',
          contentType: 'application/json',
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (response) => {
            this.unreadCommsCount = response.unread_count || 0;
          },
          error: (xhr, status, error) => {
            console.error('Error fetching unread communications count:', error);
          },
        });
      },

      /**
       * Fetch active feature flags
       */
      async fetchFeatureFlags() {
        try {
          const response = await fetch(
            `${API_BASE_URL}/verify_api/system_management_api/feature-flags/`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            }
          );
          if (!response.ok) return;
          const data = await response.json();
          const flags = data.flags || [];
          this.activeFeatureFlags = flags
            .filter(flag => flag.active)
            .map(flag => flag.name);
        } catch {
          this.activeFeatureFlags = [];
        }
      },

      /**
       * Initialize dropdown states on mount
       */
      initializeDropdowns() {
        this.processedSections.forEach(section => {
          section.items.forEach(item => {
            if (item.type === 'dropdown') {
              this.activeDropdowns[item.id] = false;
            }
          });
        });
        this.updateDropdownStates();
      }
    },

    mounted() {
      this.get_user_information();
      this.fetchUnreadCommsCount();
      this.fetchFeatureFlags();
      // Wait for config to be ready before initializing dropdowns
      this.$nextTick(() => {
        this.initializeDropdowns();
      });
    },
};
